/**
 * Validate email format
 * @param {string} email
 * @returns {string|null} error message or null if valid
 */
export function validateEmail(email) {
  if (!email) return 'Email is required';
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!re.test(email)) return 'Enter a valid email address';
  return null;
}

/**
 * Validate password strength
 * @param {string} password
 * @returns {string|null} error message or null if valid
 */
export function validatePassword(password) {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Password must contain an uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must contain a number';
  return null;
}

/**
 * Validate password confirmation matches
 * @param {string} password
 * @param {string} confirm
 * @returns {string|null}
 */
export function validatePasswordMatch(password, confirm) {
  if (!confirm) return 'Please confirm your password';
  if (password !== confirm) return 'Passwords do not match';
  return null;
}

/**
 * Validate business name
 * @param {string} name
 * @returns {string|null}
 */
export function validateBusinessName(name) {
  if (!name || !name.trim()) return 'Business name is required';
  if (name.trim().length < 2) return 'Business name must be at least 2 characters';
  return null;
}

/**
 * Validate a Liquid Bitcoin bech32 wallet address
 * Accepts testnet (tex1q...) and mainnet (ex1q...) formats
 * @param {string} address
 * @returns {string|null}
 */
export function validateLBTCAddress(address) {
  if (!address || !address.trim()) return 'Wallet address is required';

  const addr = address.trim();

  // Liquid mainnet: starts with ex1q or ex1p (segwit/taproot)
  // Liquid testnet: starts with tex1q or tex1p
  const validPrefixes = ['tex1q', 'tex1p', 'ex1q', 'ex1p'];
  const hasValidPrefix = validPrefixes.some(prefix => addr.startsWith(prefix));

  if (!hasValidPrefix) {
    return 'Address must start with tex1q or ex1q (Liquid Bitcoin bech32 format)';
  }

  // Basic length check — bech32 addresses are typically 42-62 chars
  if (addr.length < 40 || addr.length > 90) {
    return 'Invalid address length';
  }

  // Check charset: bech32 uses qpzry9x8gf2tvdw0s3jn54khce6mua7l
  const bech32Chars = /^[a-z0-9]+$/;
  const suffix = addr.slice(addr.indexOf('1') + 1);
  if (!bech32Chars.test(suffix)) {
    return 'Address contains invalid characters';
  }

  return null;
}

/**
 * Run multiple validators and return first error
 * @param {any} value
 * @param {Function[]} validators
 * @returns {string|null}
 */
export function runValidators(value, validators) {
  for (const validator of validators) {
    const error = validator(value);
    if (error) return error;
  }
  return null;
}
